import { ApiResponse } from './types';
import { apiClient } from './core/apiClient';
import { config } from '../config/environment';
import {
  IClinicalMedicalRecord,
  IMedicalRecord,
  IMedicalRecordAttachment,
  IOrthodonticsMedicalRecord,
  IPodiatryMedicalRecord,
} from '../types/interfaces/IMedicalRecord';
import { IMedicalRecordFiltersDTO } from '../types/interfaces/IMedicalRecordFilters';
import { getPatientById } from './patientService';
import { IPatient } from '../types/interfaces/IPatient';
import { useAuthStore } from '../stores/authStore';

type ClinicalEpisodeStatus = 'draft' | 'published';

interface ClinicalEpisodeSpecialtyDataDto {
  specialtyCode?: string;
  templateVersion?: string;
  data?: string | null;
}

interface ClinicalAttachmentDto {
  id: string;
  fileName: string;
  mimeType?: string | null;
  storageUrl?: string | null;
}

interface AddClinicalAttachmentPayload {
  fileName: string;
  mimeType: string;
  storageType: 'database';
  base64FileData: string;
}

interface ClinicalEpisodeDto {
  id: string;
  patientId: string;
  institutionId: string;
  appointmentId?: string | null;
  specialtyId: string;
  userId: string;
  consultationReason?: string | null;
  diagnosis?: string | null;
  notes?: string | null;
  status: ClinicalEpisodeStatus;
  createdAt: string;
  updatedAt: string;
  specialtyData?: ClinicalEpisodeSpecialtyDataDto[];
  attachments?: ClinicalAttachmentDto[];
}

interface ClinicalEpisodeMutationPayload {
  patientId: string;
  institutionId: string;
  specialtyId: string;
  consultationReason: string;
  diagnosis: string;
  notes: string;
}

interface ClinicalSpecialtyPayload {
  specialtyCode: string;
  templateVersion: string;
  data: string;
}

function getClinicalContext() {
  const { user } = useAuthStore.getState();

  if (!user?.id || !user?.institution?.id || !user?.specialty?.id) {
    throw new Error('No se encontró el contexto clínico del usuario actual');
  }

  return user;
}

function getAttachmentType(mimeType?: string | null) {
  if (!mimeType) {
    return 'document' as const;
  }

  if (mimeType.startsWith('image/')) {
    return 'image' as const;
  }

  if (mimeType === 'application/pdf') {
    return 'pdf' as const;
  }

  return 'document' as const;
}

function parseSpecialtyPayload(episode: ClinicalEpisodeDto) {
  const specialtyData = episode.specialtyData?.[0];
  const specialtyCode = specialtyData?.specialtyCode || 'clinical';

  if (!specialtyData?.data) {
    return { specialtyCode, payload: {} as Record<string, any> };
  }

  try {
    return {
      specialtyCode,
      payload: JSON.parse(specialtyData.data) as Record<string, any>,
    };
  } catch (error) {
    return { specialtyCode, payload: {} as Record<string, any> };
  }
}

function getPendingAttachments(
  attachments: IMedicalRecordAttachment[] | undefined
) {
  return (attachments || []).filter(
    attachment => attachment.isPendingUpload && attachment.base64Data
  );
}

async function uploadPendingAttachments(
  episodeId: string,
  attachments: IMedicalRecordAttachment[] | undefined
) {
  const pendingAttachments = getPendingAttachments(attachments);

  if (!pendingAttachments.length) {
    return;
  }

  for (const attachment of pendingAttachments) {
    const payload: AddClinicalAttachmentPayload = {
      fileName: attachment.name,
      mimeType: attachment.mimeType || 'application/octet-stream',
      storageType: 'database',
      base64FileData: attachment.base64Data || '',
    };

    const response = await apiClient.post<ClinicalAttachmentDto>(
      `/clinical-episodes/${episodeId}/attachments`,
      payload
    );

    if (!response.success) {
      throw new Error(response.error || 'No se pudo cargar un adjunto clínico');
    }
  }
}

function buildBaseRecord(
  episode: ClinicalEpisodeDto,
  patient: IPatient
): Omit<IMedicalRecord, 'specialty'> {
  const currentUser = useAuthStore.getState().user;

  return {
    id: episode.id,
    appointmentId: episode.appointmentId || undefined,
    patientId: episode.patientId,
    patientName: patient.fullName || patient.name || '',
    email: patient.email || '',
    phone: patient.phone || '',
    dni: patient.documentNumber || '',
    date: episode.createdAt.split('T')[0],
    doctorId: episode.userId,
    doctorName: currentUser?.id === episode.userId ? currentUser.name || '' : '',
    status: episode.status === 'draft' ? 'draft' : 'completed',
    createdAt: episode.createdAt,
    updatedAt: episode.updatedAt,
    notes: episode.notes || '',
    attachments: (episode.attachments || []).map(attachment => ({
      id: attachment.id,
      name: attachment.fileName,
      type: getAttachmentType(attachment.mimeType),
      url: attachment.storageUrl || '',
      mimeType: attachment.mimeType || undefined,
    })),
    type: 'consultation',
    diagnosis: episode.diagnosis || '',
    symptoms: [],
    treatment: '',
    institutionId: episode.institutionId,
    specialtyId: episode.specialtyId,
    observations: [],
  };
}

function mapEpisodeToMedicalRecord(
  episode: ClinicalEpisodeDto,
  patient: IPatient
): IMedicalRecord {
  const { specialtyCode, payload } = parseSpecialtyPayload(episode);
  const baseRecord = {
    ...buildBaseRecord(episode, patient),
    type: payload.type || 'consultation',
    symptoms: Array.isArray(payload.symptoms) ? payload.symptoms : [],
    treatment: payload.treatment || '',
  };

  if (specialtyCode === 'podiatry') {
    return {
      ...baseRecord,
      specialty: 'podiatry',
      footExamination: payload.footExamination || {
        leftFoot: {
          skinCondition: '',
          nailCondition: '',
          deformities: '',
          painAreas: [],
        },
        rightFoot: {
          skinCondition: '',
          nailCondition: '',
          deformities: '',
          painAreas: [],
        },
      },
      gaitAnalysis: payload.gaitAnalysis || '',
      footwearRecommendations: payload.footwearRecommendations || '',
      orthotics: payload.orthotics || {
        recommended: false,
        type: '',
        notes: '',
      },
      followUpPlan: payload.followUpPlan || '',
    } as IPodiatryMedicalRecord;
  }

  if (specialtyCode === 'orthodontics') {
    return {
      ...baseRecord,
      specialty: 'orthodontics',
      dentalExamination: payload.dentalExamination || {
        occlusion: '',
        crowding: '',
        spacing: '',
        overbite: '',
        overjet: '',
        crossbite: '',
      },
      xrayAnalysis: payload.xrayAnalysis || {
        panoramic: '',
        cephalometric: '',
        periapical: '',
      },
      treatmentPlan: payload.treatmentPlan || {
        phase: 'phase1',
        duration: '',
        appliances: [],
        goals: [],
      },
      progressNotes: payload.progressNotes || [],
      nextAppointment: payload.nextAppointment || '',
    } as IOrthodonticsMedicalRecord;
  }

  return {
    ...baseRecord,
    specialty: 'clinical',
    medications: payload.medications || [],
    vitalSigns: {
      bloodPressure: payload.vitalSigns?.bloodPressure || '',
      heartRate: payload.vitalSigns?.heartRate || '',
      temperature: payload.vitalSigns?.temperature || '',
      weight: payload.vitalSigns?.weight || '',
      height: payload.vitalSigns?.height || '',
    },
    allergies: payload.allergies || [],
    familyHistory: payload.familyHistory || '',
  } as IClinicalMedicalRecord;
}

function buildEpisodePayload(
  data: Omit<IMedicalRecord, 'id'> | Partial<IMedicalRecord>,
  institutionId: string,
  specialtyId: string
): ClinicalEpisodeMutationPayload {
  return {
    patientId: data.patientId || '',
    institutionId,
    specialtyId,
    consultationReason:
      (Array.isArray(data.symptoms) && data.symptoms.length > 0
        ? data.symptoms.join(', ')
        : data.type) || 'Consulta clínica',
    diagnosis: data.diagnosis || '',
    notes: data.notes || '',
  };
}

function buildSpecialtyPayload(
  data: Omit<IMedicalRecord, 'id'> | Partial<IMedicalRecord>
): ClinicalSpecialtyPayload {
  const specialty = data.specialty || 'clinical';

  if (specialty === 'podiatry') {
    return {
      specialtyCode: 'podiatry',
      templateVersion: 'v1',
      data: JSON.stringify({
        type: data.type,
        symptoms: data.symptoms,
        treatment: data.treatment,
        footExamination: (data as IPodiatryMedicalRecord).footExamination,
        gaitAnalysis: (data as IPodiatryMedicalRecord).gaitAnalysis,
        footwearRecommendations: (data as IPodiatryMedicalRecord)
          .footwearRecommendations,
        orthotics: (data as IPodiatryMedicalRecord).orthotics,
        followUpPlan: (data as IPodiatryMedicalRecord).followUpPlan,
      }),
    };
  }

  if (specialty === 'orthodontics') {
    return {
      specialtyCode: 'orthodontics',
      templateVersion: 'v1',
      data: JSON.stringify({
        type: data.type,
        symptoms: data.symptoms,
        treatment: data.treatment,
        dentalExamination: (data as IOrthodonticsMedicalRecord).dentalExamination,
        xrayAnalysis: (data as IOrthodonticsMedicalRecord).xrayAnalysis,
        treatmentPlan: (data as IOrthodonticsMedicalRecord).treatmentPlan,
        progressNotes: (data as IOrthodonticsMedicalRecord).progressNotes,
        nextAppointment: (data as IOrthodonticsMedicalRecord).nextAppointment,
      }),
    };
  }

  return {
    specialtyCode: 'clinical',
    templateVersion: 'v1',
    data: JSON.stringify({
      type: data.type,
      symptoms: data.symptoms,
      treatment: data.treatment,
      medications: (data as IClinicalMedicalRecord).medications,
      vitalSigns: (data as IClinicalMedicalRecord).vitalSigns,
      allergies: (data as IClinicalMedicalRecord).allergies,
      familyHistory: (data as IClinicalMedicalRecord).familyHistory,
    }),
  };
}

async function loadPatient(patientId: string, institutionId: string) {
  const response = await getPatientById(patientId, institutionId);

  if (response.success && response.data) {
    return response.data;
  }

  return {
    id: patientId,
    name: '',
    fullName: '',
    firstName: '',
    lastName: '',
    documentType: 'dni',
    isProvisionalIdentity: false,
    dateOfBirth: '',
    gender: 'other',
    phone: '',
    email: '',
    address: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    createdAt: '',
    updatedAt: '',
    isActive: true,
  } as IPatient;
}

async function hydrateEpisode(episode: ClinicalEpisodeDto) {
  const patient = await loadPatient(episode.patientId, episode.institutionId);
  return mapEpisodeToMedicalRecord(episode, patient);
}

export async function getMedicalRecords(
  filters?: IMedicalRecordFiltersDTO
): Promise<ApiResponse<IMedicalRecord[]>> {
  if (!filters?.institutionId || !filters.patientId) {
    return {
      success: true,
      data: [],
      status: 200,
    };
  }

  const response = await apiClient.get<ClinicalEpisodeDto[]>('/clinical-episodes', {
    institutionId: filters.institutionId,
    patientId: filters.patientId,
  });

  if (!response.success || !response.data) {
    return {
      ...response,
      data: [],
    };
  }

  const records = await Promise.all(response.data.map(hydrateEpisode));

  return {
    ...response,
    data: records,
  };
}

export async function getMedicalRecordById(
  id: string
): Promise<ApiResponse<IMedicalRecord>> {
  const user = getClinicalContext();
  const response = await apiClient.get<ClinicalEpisodeDto>(
    `/clinical-episodes/${id}`,
    {
      institutionId: user.institution!.id,
    }
  );

  if (!response.success || !response.data) {
    return {
      ...response,
      data: null as unknown as IMedicalRecord,
    };
  }

  return {
    ...response,
    data: await hydrateEpisode(response.data),
  };
}

export async function getMedicalRecordByAppointment(
  appointmentId: string,
  institutionId: string,
  specialtyId: string
): Promise<ApiResponse<IMedicalRecord | null>> {
  const response = await apiClient.get<ClinicalEpisodeDto>('/clinical-episodes/by-appointment', {
    appointmentId,
    institutionId,
    specialtyId,
  });

  if (!response.success || !response.data) {
    if (response.status === 404 || response.code === 'NOT_FOUND_CLINICAL_EPISODE') {
      return {
        ...response,
        success: true,
        data: null,
      } as ApiResponse<IMedicalRecord | null>;
    }

    return {
      ...response,
      data: null,
    } as ApiResponse<IMedicalRecord | null>;
  }

  return {
    ...response,
    data: await hydrateEpisode(response.data),
  } as ApiResponse<IMedicalRecord | null>;
}

export async function createMedicalRecord(
  data: Omit<IMedicalRecord, 'id'>
): Promise<ApiResponse<IMedicalRecord>> {
  const user = getClinicalContext();
  const createResponse = await apiClient.post<ClinicalEpisodeDto>(
    '/clinical-episodes',
    buildEpisodePayload(data, user.institution!.id, user.specialty!.id)
  );

  if (!createResponse.success || !createResponse.data) {
    return {
      ...createResponse,
      data: null as unknown as IMedicalRecord,
    };
  }

  const specialtyResponse = await apiClient.post<ClinicalEpisodeDto>(
    `/clinical-episodes/${createResponse.data.id}/specialty-data`,
    buildSpecialtyPayload(data)
  );

  if (!specialtyResponse.success || !specialtyResponse.data) {
    return {
      ...specialtyResponse,
      data: null as unknown as IMedicalRecord,
    };
  }

  let episode = specialtyResponse.data;

  await uploadPendingAttachments(createResponse.data.id, data.attachments);

  if (data.status !== 'draft') {
    const publishResponse = await apiClient.post<ClinicalEpisodeDto>(
      `/clinical-episodes/${createResponse.data.id}/publish`,
      {}
    );

    if (!publishResponse.success || !publishResponse.data) {
      return {
        ...publishResponse,
        data: null as unknown as IMedicalRecord,
      };
    }

    episode = publishResponse.data;
  }

  const hydratedResponse = await getMedicalRecordById(episode.id);

  return {
    ...createResponse,
    data: hydratedResponse.data,
  };
}

export async function updateMedicalRecord(
  id: string,
  data: Partial<IMedicalRecord>
): Promise<ApiResponse<IMedicalRecord>> {
  const user = getClinicalContext();
  const updateResponse = await apiClient.put<ClinicalEpisodeDto>(
    `/clinical-episodes/${id}`,
    buildEpisodePayload(data, user.institution!.id, user.specialty!.id)
  );

  if (!updateResponse.success || !updateResponse.data) {
    return {
      ...updateResponse,
      data: null as unknown as IMedicalRecord,
    };
  }

  const specialtyResponse = await apiClient.post<ClinicalEpisodeDto>(
    `/clinical-episodes/${id}/specialty-data`,
    buildSpecialtyPayload({
      ...updateResponse.data,
      ...data,
      specialty: data.specialty || user.specialty!.code || 'clinical',
    } as Partial<IMedicalRecord>)
  );

  if (!specialtyResponse.success || !specialtyResponse.data) {
    return {
      ...specialtyResponse,
      data: null as unknown as IMedicalRecord,
    };
  }

  let episode = specialtyResponse.data;

  await uploadPendingAttachments(id, data.attachments);

  if (data.status !== 'draft' && updateResponse.data.status === 'draft') {
    const publishResponse = await apiClient.post<ClinicalEpisodeDto>(
      `/clinical-episodes/${id}/publish`,
      {}
    );

    if (!publishResponse.success || !publishResponse.data) {
      return {
        ...publishResponse,
        data: null as unknown as IMedicalRecord,
      };
    }

    episode = publishResponse.data;
  }

  const hydratedResponse = await getMedicalRecordById(episode.id);

  return {
    ...updateResponse,
    data: hydratedResponse.data,
  };
}

export async function deleteMedicalRecord(
  _id: string
): Promise<ApiResponse<boolean>> {
  return {
    success: false,
    data: false,
    status: 409,
    error: 'El backend clínico actual no permite eliminar evoluciones.',
  };
}

export async function exportMedicalRecord(
  id: string
): Promise<ApiResponse<string>> {
  try {
    const response = await fetch(`${config.api.baseURL}/clinical-episodes/${id}/export`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/pdf',
      },
    });

    if (!response.ok) {
      return {
        success: false,
        data: null as unknown as string,
        status: response.status,
        error:
          response.status === 404
            ? 'La exportación PDF todavía no está disponible en backend.'
            : 'No se pudo exportar la historia clínica.',
      };
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);

    return {
      success: true,
      data: url,
      status: 200,
    };
  } catch {
    return {
      success: false,
      data: null as unknown as string,
      status: 500,
      error: 'No se pudo exportar la historia clínica.',
    };
  }
}
